import { Rewind, Play, Pause, FastForward, VolumeX, Volume2, Loader2, AlertTriangle } from 'lucide-react'
import { useSpeech, SEEK_STEP_SECONDS, formatAudioTime } from '../lib/speechPlayer'

export function ListenButton({ id, text, label = 'Listen to reply' }: { id: string; text: string; label?: string }) {
  const { speaking, paused, hasTransport, loading, error, progress, speak, stop, pause, resume, seekBy, seekTo } = useSpeech(id)
  const active = speaking || loading
  // Error only shows while idle (cleared the moment a fresh play starts), so a
  // click in the error state falls through to speak() — i.e. tap-to-retry.
  const showError = error && !active
  const controlBtn = 'inline-flex items-center justify-center h-6 w-6 rounded transition-colors hover:bg-secondary/40 text-muted-foreground/60 hover:text-foreground'

  // Active playback expands into a compact transport: ±10s, pause/resume,
  // stop. Seek/pause drive the module-level <audio> element, so they work
  // from any mounted button (and mirror the lock-screen controls). The
  // browser-voice fallback has no element to seek — it keeps stop-only below.
  if (speaking && !loading && hasTransport) {
    return (
      <span className="inline-flex items-center gap-1 min-w-0">
        <button type="button" onClick={() => seekBy(-SEEK_STEP_SECONDS)} aria-label="Back 10 seconds" title="Back 10s" className={controlBtn}>
          <Rewind className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => (paused ? resume() : pause())}
          aria-label={paused ? 'Resume reading' : 'Pause reading'}
          title={paused ? 'Resume' : 'Pause'}
          className={controlBtn}
        >
          {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={() => seekBy(SEEK_STEP_SECONDS)} aria-label="Forward 10 seconds" title="Forward 10s" className={controlBtn}>
          <FastForward className="h-3.5 w-3.5" />
        </button>
        {/* Progress bar — the duration keeps growing while later chunks are
            still being synthesized, so the track can lengthen mid-play. */}
        <input
          type="range"
          min={0}
          max={Math.max(progress.duration, 0.1)}
          step={0.1}
          value={Math.min(progress.position, progress.duration)}
          onChange={(e) => seekTo(parseFloat(e.target.value))}
          aria-label="Audio position"
          className="w-20 sm:w-32 h-1 accent-primary cursor-pointer"
        />
        <span className="text-[10px] tabular-nums text-muted-foreground/70 shrink-0">
          {formatAudioTime(progress.position)}/{formatAudioTime(progress.duration)}
        </span>
        <button type="button" onClick={stop} aria-label="Stop reading" title="Stop" className={controlBtn}>
          <VolumeX className="h-3.5 w-3.5" />
        </button>
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => (active ? stop() : speak(text))}
      aria-label={loading ? 'Loading audio' : speaking ? 'Stop reading' : showError ? 'Playback failed, tap to retry' : label}
      title={loading ? 'Loading…' : speaking ? 'Stop reading' : showError ? "Couldn't play audio — tap to retry" : label}
      className={`inline-flex items-center justify-center h-6 w-6 rounded transition-colors hover:bg-secondary/40 ${
        showError ? 'text-yellow-500 hover:text-yellow-500' : 'text-muted-foreground/60 hover:text-foreground'
      }`}
    >
      {loading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : speaking
          ? <VolumeX className="h-3.5 w-3.5" />
          : showError
            ? <AlertTriangle className="h-3.5 w-3.5" />
            : <Volume2 className="h-3.5 w-3.5" />}
    </button>
  )
}
